import React, { useState } from 'react';
import styled from 'styled-components';
import { useAuthStore } from '../../store/authStore';
import glassTheme, { glassMixins } from '../../styles/glassTheme';

const FormContainer = styled.div`
  ${glassTheme.glass.heavy}
  padding: 3rem;
  width: 90%;
  max-width: 450px;
  box-shadow: ${glassTheme.shadow.xl}, ${glassTheme.shadow.glow};
`;

const FormTitle = styled.h2`
  text-align: center;
  margin-bottom: 2rem;
  font-size: 2rem;
  background: ${glassTheme.gradients.cyber};
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 0.5rem;
  color: ${glassTheme.colors.text.primary};
  font-weight: 500;
`;

const Input = styled.input`
  ${glassMixins.input}
  width: 100%;
  padding: 1rem;

  &:focus {
    outline: none;
    border-color: ${glassTheme.colors.accent.blue};
    background: rgba(0, 0, 0, 0.7);
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.2);
  }

  &::placeholder {
    color: ${glassTheme.colors.text.muted};
  }
`;

const Button = styled.button`
  ${glassMixins.button}
  width: 100%;
  padding: 1rem 2rem;
  margin-bottom: 1rem;

  &:hover {
    background: ${glassTheme.gradients.cyber};
    box-shadow: ${glassTheme.shadow.xl}, ${glassTheme.shadow.glow};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
`;

const ErrorMessage = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== 'visible',
})<{ visible: boolean }>`
  color: ${glassTheme.colors.status.error};
  font-size: 0.9rem;
  margin-top: 0.5rem;
  text-align: center;
  display: ${props => props.visible ? 'block' : 'none'};
`;

const FormFooter = styled.div`
  text-align: center;
  color: ${glassTheme.colors.text.secondary};
  margin-top: 2rem;

  a {
    color: ${glassTheme.colors.accent.blue};
    text-decoration: none;
    cursor: pointer;

    &:hover {
      text-decoration: underline;
    }
  }
`;

const Spinner = styled.div`
  border: 2px solid ${glassTheme.colors.border};
  border-radius: 50%;
  border-top: 2px solid ${glassTheme.colors.accent.blue};
  width: 16px;
  height: 16px;
  animation: spin 1s linear infinite;

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

interface LoginFormProps {
  onSwitchToRegister: () => void;
  onClose: () => void;
}

export const LoginForm: React.FC<LoginFormProps> = ({ onSwitchToRegister, onClose }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const { login, isLoading, error, clearError } = useAuthStore();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    clearError();

    const success = await login(email, password);
    if (success) {
      onClose();
    }
  };

  const handleInputChange = (setter: React.Dispatch<React.SetStateAction<string>>) => {
    return (e: React.ChangeEvent<HTMLInputElement>) => {
      setter(e.target.value);
      if (error) clearError();
    };
  };

  return (
    <FormContainer>
      <FormTitle>欢迎回来</FormTitle>
      <form onSubmit={handleSubmit}>
        <FormGroup>
          <Label htmlFor="email">邮箱地址</Label>
          <Input
            id="email"
            type="email"
            placeholder="请输入您的邮箱"
            value={email}
            onChange={handleInputChange(setEmail)}
            required
            disabled={isLoading}
          />
        </FormGroup>
        <FormGroup>
          <Label htmlFor="password">密码</Label>
          <Input
            id="password"
            type="password"
            placeholder="请输入密码"
            value={password}
            onChange={handleInputChange(setPassword)}
            required
            disabled={isLoading}
          />
        </FormGroup>
        <ErrorMessage visible={!!error}>
          {error}
        </ErrorMessage>
        <Button type="submit" disabled={isLoading}>
          {isLoading ? (
            <>
              <Spinner />
              登录中...
            </>
          ) : (
            <>
              <i className="fas fa-sign-in-alt"></i>
              登录
            </>
          )}
        </Button>
      </form>
      <FormFooter>
        还没有账户？<button type="button" onClick={onSwitchToRegister} style={{
          background: 'none',
          border: 'none',
          color: glassTheme.colors.accent.blue,
          textDecoration: 'underline',
          cursor: 'pointer',
          padding: 0,
          font: 'inherit'
        }}>立即注册</button>
      </FormFooter>
    </FormContainer>
  );
};